import React from 'react'
import styled from 'styled-components'
import { Body, Title } from 'src/Styles/text-styles'
import RecommendationView from 'src/SpeedTestDetail/RecommendationView'
import SourceView from 'src/SpeedTestDetail/SourceView'
import BackButton from 'src/Component/BackButton'
import ScrollToTop from 'src/Component/ScrollToTop'
import { localize } from 'src/lib/localize'

const Container = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;

  @media (min-width: 320px) {
    flex: 1;
    min-width: 220px;
  }

  @media (min-width: 420px) {
    flex: 1;
    min-width: 320px;
  }

  @media (min-width: 500px) {
    flex: 1;
    min-width: 400px;
  }

  @media (min-width: 769px) {
    min-width: 0px;
  }
`

const BoldBody = styled(Body)`
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 0.5px;
  @media (max-width: 768px) {
    margin-top: 8px;
  }

  @media (min-width: 768px) {
    margin-top 16px;
  }
`
const DescriptionBody = styled(Body)`
  line-height: 28px;
  letter-spacing: 0.5px;
`
const Space = styled.div`
  margin-bottom: 24px;
`

const HeaderTitle = styled(Title)`
  line-height: 28px;
  letter-spacing: 0.5px;
`

const TitleText = localize('TITLE')('Understanding Speed Check')

const SpeedMeansTitle = localize('LABEL')('What does your speed mean?')
const SpeedMeansDescription1 = localize('LABEL')(
  'Your connection speed reveals how quickly data is transferred between your device and the internet. Your download speed measures the rate that data travels from the internet to your computer. Your upload speed measures the rate at which data travels from your computer back to the internet. Normally, the download speed is much greater than the upload speed.'
)
const SpeedMeansDescription2 = localize('LABEL')(
  'These speeds are measured in megabits per second or Mbps. For reference, a broadband internet connection is defined as having a minimum download speed of 25 Mbps and a minimum upload of 3 Mbps. A connection at or above 25 Mbps is considered the minimum speed necessary for most online activities, including video streaming, gaming, web browsing and downloading music.'
)

const DownloadUploadPingTitle = localize('LABEL')(
  'What is download and upload speed?'
)
const DownloadUploadPingDescription = localize('LABEL')(
  'Download speed is the rate at which data is transferred from the Internet to your device. Upload speed is the opposite, the rate at which data is transferred from your device to the Internet. The speeds for both download and upload are measured in Megabits per second (Mbps), with higher values representing better performance through faster data transfer. Most connections have a balance that favors faster download versus upload as the majority of people benefit from faster speed to download video or other content. Upload does play an important factor in activities such as online gaming where more data is required as you share the experience with other online players in real time.'
)

const PingTitle = localize('LABEL')('What is ping?')
const PingDescription = localize('LABEL')(
  'Ping is the amount of time it takes for another device on the Internet, such as a computer or server, to return a response to your device. The lower the ping the better because this means any command sent or received is faster, with values between 1 and 100 milliseconds enabling the best experience. For example, the lower ping will help web pages load as quickly as possible and will see the most advantages while playing games online that are consistently communicating to the Internet.'
)

const TipConsiderationTitle = localize('LABEL')('Tips to consider:')
const NetworkBandwidthTitle = 'Network bandwidth:'
const NetworkBandwidthDescription = localize('LABEL')(
  ' Network bandwidth is the amount of data that can be transferred by a network in a given period of time. This is important as many households have multiple users on the same Internet connection, and the result is that the maximum download and upload speeds will be divided between these users. For example, on a 35Mbps connection one device may be downloading files (using 35Mbps) and the other is streaming high-definition video content (requires 15Mbps), but the video is not loading quickly because the two are competing for the same connection and there is not enough bandwidth. A higher connection speed of 50Mbps or ending use of one of the activities would be needed in order for things to function better.'
)
const SpeedPerformanceTitle = localize('LABEL')(
  'Speed and performance with wireless (Wi-Fi) connections:'
)
const SpeedPerformanceDescription = localize('LABEL')(
  'Wireless connections are impacted by the same factors as wired connections with a few key differences. Wi-Fi must contend with the distance a signal has to travel between your device and router, as well as any obstacles between the two. Connection performance is directly related to the strength of the signal and any degradation is known as signal drop. For example, walking around the home through different rooms may net different Internet performance due to distance and the number of walls, floors, ceilings, and other obstacles that can hinder signal travel.'
)

const SpeedTestDetail = () => {
  return (
    <Container>
      <ScrollToTop />
      <BackButton />
      <HeaderTitle>{TitleText}</HeaderTitle>
      <Space />

      <BoldBody>{SpeedMeansTitle}</BoldBody>
      <DescriptionBody>{SpeedMeansDescription1}</DescriptionBody>
      <DescriptionBody>{SpeedMeansDescription2}</DescriptionBody>

      <Space />
      <BoldBody>{DownloadUploadPingTitle}</BoldBody>
      <DescriptionBody>{DownloadUploadPingDescription}</DescriptionBody>

      <Space />
      <BoldBody>{PingTitle}</BoldBody>
      <DescriptionBody>{PingDescription}</DescriptionBody>

      <Space />
      <BoldBody>{TipConsiderationTitle}</BoldBody>
      <DescriptionBody>
        <BoldBody style={{ marginTop: 0 }}>{NetworkBandwidthTitle}</BoldBody>
        {NetworkBandwidthDescription}
      </DescriptionBody>

      <DescriptionBody>
        <BoldBody style={{ marginTop: 0 }}>{SpeedPerformanceTitle}</BoldBody>
        {SpeedPerformanceDescription}
      </DescriptionBody>

      <Space />
      <RecommendationView />

      <Space />
      <SourceView />
    </Container>
  )
}

export default SpeedTestDetail
